import { ProgrammeSettingsView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.types';
import { Tree } from '@dealstack/frontend/shared/components/Tree/Tree';
import { TreeItem } from '@dealstack/frontend/shared/components/Tree/utils';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { MipProgrammeEntity } from '@dealstack/frontend/shared/dataExchange/services/mip.service';

const SECURITY_ENTITY_ITEM_PREFIX = 'security-entity:';

interface Props {
	title?: string;
	currentView: ProgrammeSettingsView;
	onViewChange: (view: ProgrammeSettingsView) => void;
	programmeEntities?: MipProgrammeEntity[];
	selectedSecurityEntityId?: string;
	onSecurityEntityChange?: (entityId: string) => void;
}

const emailTemplatesItem = (
	currentView: ProgrammeSettingsView,
	label: string
): TreeItem<ProgrammeSettingsView> => ({
	id: ProgrammeSettingsView.EmailTemplates,
	name: label,
	icon: 'mail',
	selected: currentView === ProgrammeSettingsView.EmailTemplates,
});

export const ProgrammeSettingsSidebar = ({
	currentView,
	onViewChange,
	programmeEntities = [],
	selectedSecurityEntityId,
	onSecurityEntityChange,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { value: automatedRemindersEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.AUTOMATED_EMAIL_REMINDERS
	);

	const securitiesChildren: TreeItem[] = programmeEntities.map((entity) => ({
		id: `${SECURITY_ENTITY_ITEM_PREFIX}${entity.entityId}`,
		name: entity.displayName,
		icon: 'building-office-company-entity-line',
		selected:
			currentView === ProgrammeSettingsView.Securities &&
			selectedSecurityEntityId === entity.entityId,
	}));

	const menuItems: TreeItem[] = [
		{
			id: ProgrammeSettingsView.General,
			name: tMipManage('programme_settings.labels.overview'),
			icon: 'plant-grown',
			selected: currentView === ProgrammeSettingsView.General,
		},
		{
			id: ProgrammeSettingsView.Members,
			name: tMipManage('programme_settings.labels.members'),
			icon: 'people-users',
			selected: currentView === ProgrammeSettingsView.Members,
		},
		{
			id: ProgrammeSettingsView.Securities,
			name: tMipManage('programme_settings.labels.securities'),
			icon: 'money-v3',
			selected:
				currentView === ProgrammeSettingsView.Securities && securitiesChildren.length === 0,
			expanded: currentView === ProgrammeSettingsView.Securities,
			isFolder: securitiesChildren.length > 0,
			children: securitiesChildren,
		},
		{
			id: ProgrammeSettingsView.Loans,
			name: tMipManage('programme_settings.labels.loans'),
			icon: 'briefcase',
			selected: currentView === ProgrammeSettingsView.Loans,
		},
		{
			id: ProgrammeSettingsView.MasterDocuments,
			name: tMipManage('programme_settings.labels.documents'),
			icon: 'master-document',
			selected: currentView === ProgrammeSettingsView.MasterDocuments,
		},
		{
			id: ProgrammeSettingsView.InvestorData,
			name: tMipManage('programme_settings.labels.investor_data'),
			icon: 'user-settings',
			selected: currentView === ProgrammeSettingsView.InvestorData,
		},
		{
			id: ProgrammeSettingsView.KycSettings,
			name: tMipManage('programme_settings.labels.kyc'),
			icon: 'verified-v3',
			selected: currentView === ProgrammeSettingsView.KycSettings,
		},
		{
			id: ProgrammeSettingsView.Tax,
			name: tMipManage('programme_settings.labels.tax'),
			icon: 'percentage',
			selected: currentView === ProgrammeSettingsView.Tax,
		},
		{
			id: ProgrammeSettingsView.Classifiers,
			name: tMipManage('programme_settings.labels.classifiers'),
			icon: 'group',
			selected: currentView === ProgrammeSettingsView.Classifiers,
		},
		emailTemplatesItem(currentView, tMipManage('programme_settings.labels.email_templates')),
		...(automatedRemindersEnabled
			? ([
					{
						id: ProgrammeSettingsView.AutomatedEmails,
						name: tMipManage('programme_settings.labels.automated_emails'),
						icon: 'mail',
						selected: currentView === ProgrammeSettingsView.AutomatedEmails,
					},
				] satisfies TreeItem<ProgrammeSettingsView>[])
			: []),
	];

	const handleItemClick = (item: TreeItem) => {
		if (item.id.startsWith(SECURITY_ENTITY_ITEM_PREFIX)) {
			onSecurityEntityChange?.(item.id.replace(SECURITY_ENTITY_ITEM_PREFIX, ''));
			return;
		}

		if (item.id === ProgrammeSettingsView.Securities && programmeEntities.length > 0) {
			onSecurityEntityChange?.(selectedSecurityEntityId ?? programmeEntities[0].entityId);
			return;
		}

		onViewChange(item.id as ProgrammeSettingsView);
	};

	return <Tree items={menuItems} onItemClick={handleItemClick} />;
};
